import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { CheckSquare, Circle, CheckCircle2, Clock, GitBranch } from 'lucide-react';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = [
  { id: '1', name: 'Complete safety training review', subtasks: [{ completed: true }, { completed: false }], due_date: new Date().toISOString(), priority: 'high', project_id: '1', projects: { name: 'Site Alpha' } },
  { id: '2', name: 'Update COSHH assessments', subtasks: [{ completed: false }], due_date: new Date(Date.now() + 86400000).toISOString(), priority: 'urgent', project_id: '1', projects: { name: 'Main Office' } },
  { id: '3', name: 'Schedule fire drill', subtasks: [], due_date: new Date(Date.now() + 172800000).toISOString(), priority: null, project_id: '2', projects: { name: 'Warehouse B' } },
  { id: '4', name: 'Review contractor permits', subtasks: [{ completed: true }, { completed: true }], due_date: new Date(Date.now() - 86400000).toISOString(), priority: 'high', project_id: '1', projects: { name: 'Site Alpha' } },
  { id: '5', name: 'Update PPE inventory', subtasks: [{ completed: false }, { completed: false }, { completed: true }], due_date: new Date(Date.now() + 345600000).toISOString(), priority: null, project_id: '3', projects: { name: 'Distribution Center' } },
];

export function MyTasksTile({ filter, useMockData }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!userProfile?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        // Fetch regular tasks
        let taskQuery = supabase
          .from('tasks')
          .select(`
            id,
            name,
            subtasks,
            due_date,
            priority,
            project_id,
            assigned_to,
            projects(name)
          `)
          .order('due_date', { ascending: true, nullsFirst: false })
          .limit(50);

        if (filter?.spaceId) {
          taskQuery = taskQuery.eq('project_id', filter.spaceId);
        }

        const { data: tasks, error: tasksError } = await taskQuery;
        if (tasksError) throw tasksError;

        // Filter to tasks assigned to current user
        const myTasks = (tasks || []).filter(task => {
          return Array.isArray(task.assigned_to) && task.assigned_to.includes(userProfile.id);
        });

        // Filter out completed tasks (all subtasks done)
        const activeRegularTasks = myTasks.filter(task => {
          if (!task.subtasks || task.subtasks.length === 0) return true;
          const completedCount = task.subtasks.filter(st => st.completed).length;
          return completedCount < task.subtasks.length;
        }).map(task => ({ ...task, isWorkflowTask: false }));

        // Fetch workflow task runs
        let workflowQuery = supabase
          .from('checklists_workflow_runs')
          .select(`
            id,
            run_name,
            project_id,
            status,
            started_by,
            workflows(id, name, tasks),
            projects(name)
          `)
          .eq('status', 'in_progress');

        if (filter?.spaceId) {
          workflowQuery = workflowQuery.eq('project_id', filter.spaceId);
        }

        const { data: workflowRuns, error: runsError } = await workflowQuery;
        if (runsError) throw runsError;

        let workflowTasks = [];

        if (workflowRuns && workflowRuns.length > 0) {
          const runIds = workflowRuns.map(r => r.id);

          const { data: taskRuns, error: taskRunsError } = await supabase
            .from('checklists_workflow_task_runs')
            .select('*')
            .in('workflow_run_id', runIds)
            .neq('status', 'completed');

          if (taskRunsError) throw taskRunsError;

          // Filter to tasks assigned to current user
          const myTaskRuns = (taskRuns || []).filter(taskRun => {
            const assignees = taskRun.assignees || [];
            // Handle both object format {type, id/user_id} and simple string format
            return assignees.some(a => {
              if (typeof a === 'string') return a === userProfile.id;
              if (a.type === 'user') return a.id === userProfile.id || a.user_id === userProfile.id;
              if (a.type === 'workflow_runner') {
                // Check if current user started this workflow run
                const workflowRun = workflowRuns.find(r => r.id === taskRun.workflow_run_id);
                return workflowRun?.started_by === userProfile.id;
              }
              return false;
            });
          });

          // Map to unified format
          workflowTasks = myTaskRuns.map(taskRun => {
            const workflowRun = workflowRuns.find(r => r.id === taskRun.workflow_run_id);
            const workflow = workflowRun?.workflows;
            const workflowTaskDef = (workflow?.tasks || []).find(t => String(t.id) === taskRun.task_id);

            return {
              id: taskRun.id,
              name: workflowTaskDef?.formTitle || workflowTaskDef?.name || `Task ${taskRun.task_order + 1}`,
              due_date: taskRun.due_date,
              project_id: workflowRun?.project_id,
              projects: workflowRun?.projects,
              workflow_run_id: taskRun.workflow_run_id,
              workflow_run_name: workflowRun?.run_name,
              workflow_name: workflow?.name,
              isWorkflowTask: true,
              status: taskRun.status,
            };
          });
        }

        // Combine and sort by due date
        const allTasks = [...activeRegularTasks, ...workflowTasks]
          .sort((a, b) => {
            if (!a.due_date && !b.due_date) return 0;
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return new Date(a.due_date) - new Date(b.due_date);
          })
          .slice(0, 10);

        setData(allTasks);
      } catch (err) {
        console.error('Failed to load tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userProfile?.id, filter?.spaceId, useMockData]);

  // Determine layout based on dimensions
  const { width, height } = dimensions;
  const isXS = width < 150 || height < 100;
  const isSmall = width < 200 || height < 140;
  const isWide = width >= 450; // Wide enough for table columns
  const isLarge = width >= 300 && height >= 280;

  // Calculate how many items to show
  const getVisibleCount = () => {
    if (isXS) return 0;
    if (isSmall) return 2;
    const itemHeight = 52;
    const availableHeight = height - 20;
    return Math.max(1, Math.floor(availableHeight / itemHeight));
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isPast(date)) return `${Math.ceil((new Date() - date) / (1000 * 60 * 60 * 24))}d overdue`;
    return format(date, 'MMM d');
  };

  const getDueDateColor = (dateStr) => {
    if (!dateStr) return 'secondary';
    const date = new Date(dateStr);
    if (isPast(date) && !isToday(date)) return 'destructive';
    if (isToday(date)) return 'warning';
    return 'secondary';
  };

  const getTaskProgress = (task) => {
    if (!task.subtasks || task.subtasks.length === 0) return null;
    const completed = task.subtasks.filter(st => st.completed).length;
    const total = task.subtasks.length;
    return { completed, total, percent: Math.round((completed / total) * 100) };
  };

  const getTaskIcon = (task) => {
    if (task.isWorkflowTask) {
      return <GitBranch className="h-4 w-4 text-blue-500" />;
    }
    const progress = getTaskProgress(task);
    if (!progress) return <Circle className="h-4 w-4 text-gray-400" />;
    if (progress.percent === 100) return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    if (progress.percent > 0) return <Clock className="h-4 w-4 text-blue-500" />;
    return <Circle className="h-4 w-4 text-gray-400" />;
  };

  const handleTaskClick = (task) => {
    if (task.isWorkflowTask && task.workflow_run_id) {
      navigate(`/space/${task.project_id}/workflow-run/${task.workflow_run_id}`);
    } else {
      navigate(`/space/${task.project_id}/tasks`);
    }
  };

  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full">
        <div className="animate-pulse space-y-2 w-full p-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-200" />
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded w-3/4 mb-1" />
                <div className="h-2 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full text-gray-400 overflow-visible">
        <CheckSquare className="h-8 w-8 mb-2" />
        <span className="text-sm">No tasks</span>
      </div>
    );
  }

  const visibleItems = data.slice(0, getVisibleCount());
  const remainingCount = data.length - visibleItems.length;

  // XS Layout: Just count
  if (isXS) {
    const overdueCount = data.filter(t => t.due_date && isPast(new Date(t.due_date)) && !isToday(new Date(t.due_date))).length;
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full overflow-visible">
        <div className="text-center">
          <div className={cn('text-3xl font-bold leading-none', overdueCount > 0 ? 'text-red-600' : 'text-gray-700')}>
            {data.length}
          </div>
          <p className="text-xs text-gray-500 mt-1">tasks</p>
        </div>
      </div>
    );
  }

  // Small Layout: Count + top task
  if (isSmall) {
    const firstTask = data[0];
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2 overflow-visible">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-2xl font-bold text-gray-700">{data.length}</div>
          <span className="text-xs text-gray-500">tasks</span>
        </div>
        {firstTask && (
          <div
            onClick={() => handleTaskClick(firstTask)}
            className="flex items-center gap-2 p-1.5 rounded bg-gray-50 cursor-pointer hover:bg-gray-100"
          >
            {getTaskIcon(firstTask)}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{firstTask.name}</p>
              {firstTask.due_date && (
                <p className={cn('text-[10px]', getDueDateColor(firstTask.due_date) === 'destructive' ? 'text-red-600' : 'text-gray-500')}>
                  {formatDueDate(firstTask.due_date)}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Wide/Large Layout: Table view with columns
  if (isWide || isLarge) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2">
        {/* Table Header */}
        <div className={cn(
          "grid gap-2 pb-2 border-b text-xs font-medium text-gray-500",
          isWide ? "grid-cols-[1fr_80px_120px_100px]" : "grid-cols-[1fr_80px_100px]"
        )}>
          <div className="flex items-center gap-1">
            <CheckSquare className="h-3.5 w-3.5" />
            <span>Name</span>
          </div>
          <div>Due</div>
          {isWide && <div>Workflow Run</div>}
          <div>Workflow</div>
        </div>

        {/* Table Body */}
        <div className="flex-1 overflow-y-auto">
          {visibleItems.map((task) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(task)}
              className={cn(
                "grid gap-2 py-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors items-center text-sm",
                isWide ? "grid-cols-[1fr_80px_120px_100px]" : "grid-cols-[1fr_80px_100px]"
              )}
            >
              {/* Name */}
              <div className="flex items-center gap-2 min-w-0">
                {getTaskIcon(task)}
                <span className="truncate font-medium">{task.name}</span>
              </div>

              {/* Due Date */}
              <div className={cn(
                "text-xs",
                task.due_date && getDueDateColor(task.due_date) === 'destructive' ? 'text-red-600 font-medium' :
                task.due_date && getDueDateColor(task.due_date) === 'warning' ? 'text-amber-600' : 'text-gray-500'
              )}>
                {task.due_date ? formatDueDate(task.due_date) : '-'}
              </div>

              {/* Workflow Run (wide only) */}
              {isWide && (
                <div className="text-xs text-gray-600 truncate">
                  {task.isWorkflowTask ? task.workflow_run_name : '-'}
                </div>
              )}

              {/* Workflow/Space */}
              <div className="text-xs text-purple-600 truncate">
                {task.isWorkflowTask ? task.workflow_name : task.projects?.name}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {remainingCount > 0 && (
          <button
            onClick={() => navigate('/tasks')}
            className="mt-2 pt-2 border-t text-center text-sm text-blue-600 hover:text-blue-700"
          >
            +{remainingCount} more
          </button>
        )}
      </div>
    );
  }

  // Default/Medium Layout
  return (
    <div ref={containerRef} className="flex flex-col h-full p-2">
      <div className="flex-1 space-y-1">
        {visibleItems.map((task) => {
          const progress = getTaskProgress(task);
          return (
            <div
              key={task.id}
              onClick={() => handleTaskClick(task)}
              className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              {getTaskIcon(task)}

              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{task.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[10px] text-gray-500 truncate">
                    {task.isWorkflowTask ? task.workflow_name : task.projects?.name}
                  </span>
                  {!task.isWorkflowTask && progress && (
                    <span className="text-[10px] text-gray-400">{progress.completed}/{progress.total}</span>
                  )}
                </div>
              </div>

              {task.due_date && (
                <Badge variant={getDueDateColor(task.due_date)} className="shrink-0 text-[10px] px-1.5 py-0">
                  {formatDueDate(task.due_date)}
                </Badge>
              )}
            </div>
          );
        })}
      </div>

      {remainingCount > 0 && (
        <button
          onClick={() => navigate('/tasks')}
          className="text-xs text-blue-600 hover:text-blue-700 pt-1"
        >
          +{remainingCount} more
        </button>
      )}
    </div>
  );
}
